import React, { useState, useEffect, useCallback } from "react";
import Axios from "axios";
import { useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import prenatal from "../../../Icons/Avtar/Prenatal.png";
import postnatal from "../../../Icons/Avtar/Postnatal.png";
import useContactAnalytics from "./useContactAnalytics";
import { ContactPrenatalMeta } from "./ContactSeoMeta";

import {
  Section, Inner, BgBlob, DotGrid,
  HeroContent, HeroEyebrow, HeroTitle, HeroDivider, HeroDesc,
  FormBlock, FormLayout,
  SidePanel, SidePanelImg, SidePanelOverlay, SidePanelMeta,
  PanelEyebrow, PanelTitle, PanelAccent, PanelContact,
  FormContent, FormTitle, FormSubtitle,
  ToggleRow, ToggleBtn,
  FieldGrid, FieldWrap, FieldLabel, InputField, TextAreaField,
  FieldError, FieldHint,
  SubmitBtn, SpinnerRing,
  SuccessWrap, SuccessIcon, SuccessTitle, SuccessDesc,
  ContactLine, DynamicFields,
} from "./ContactUs.styles";

// ─── Constants ─────────────────────────────────────────────────────────────────

const API_URL =
  "https://us-central1-website-a1336.cloudfunctions.net/api/registerPrenatal";

const INITIAL_FORM = {
  name:               "",
  email:              "",
  country:            "",
  mobile:             "",
  pregnancyDetails:   "",
  complications:      "",
  babyAge:            "",
  postpartumConcerns: "",
};

// ─── Icons ────────────────────────────────────────────────────────────────────

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .82h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.75a16 16 0 006.29 6.29l1.29-1.29a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7a2 2 0 011.72 2.02z" />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ─── ContactUSPrenatal ────────────────────────────────────────────────────────

const ContactUSPrenatal = () => {
  const navigate  = useNavigate();
  const analytics = useContactAnalytics();

  const [isPrenatal, setIsPrenatal] = useState(true);
  const [formData,   setFormData]   = useState(INITIAL_FORM);
  const [errors,     setErrors]     = useState({});
  const [loading,    setLoading]    = useState(false);
  const [showModal,  setShowModal]  = useState(false);

  // ── Form view on mount ──
  useEffect(() => {
    analytics.trackFormView("prenatal", window.location.href);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Validation ──
  const validate = useCallback((data) => {
    const e = {};
    if (!data.name.trim())                               e.name    = "Full name is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) e.email   = "Valid email required";
    if (!data.country.trim())                            e.country = "Country code required";
    if (!/^\+?[\d\s-]{7,15}$/.test(data.mobile))        e.mobile  = "Valid phone required";
    return e;
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev)    => ({ ...prev, [name]: undefined }));
  }, []);

  // Reset dynamic fields and track toggle when switching Prenatal ↔ Postnatal
  const handleToggle = useCallback((value) => {
    setIsPrenatal(value);
    analytics.trackPrenatalToggle(value ? "prenatal" : "postnatal");
    setFormData((prev) => ({
      ...prev,
      pregnancyDetails:   "",
      complications:      "",
      babyAge:            "",
      postpartumConcerns: "",
    }));
  }, [analytics]);

  const handleModalClose = useCallback(() => {
    setShowModal(false);
    navigate("/");
  }, [navigate]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const errs = validate(formData);
    const hasErrors = Object.keys(errs).length > 0;

    analytics.trackSubmitAttempt("prenatal", hasErrors);
    if (hasErrors) { setErrors(errs); return; }

    try {
      setLoading(true);
      const payload = { ...formData, isPrenatal, source: "Yoga-Shastra" };
      const res = await Axios.post(API_URL, payload);
      if (res.data.code === "NP001") {
        analytics.trackSubmitSuccess("prenatal", {
          yoga_type: isPrenatal ? "prenatal" : "postnatal",
        });
        setShowModal(true);
        setFormData(INITIAL_FORM);
        setTimeout(handleModalClose, 5000);
      }
    } catch (err) {
      console.error("Prenatal form submission error:", err);
      analytics.trackSubmitError("prenatal", "network");
    } finally {
      setLoading(false);
    }
  }, [formData, isPrenatal, validate, handleModalClose, analytics]);

  const currentImage = isPrenatal ? prenatal : postnatal;
  const imageAlt     = isPrenatal ? "Prenatal yoga" : "Postnatal yoga";

  return (
    <>
      <Section id="prenatal-contact">

        {/* ── SEO — ContactPrenatalMeta mirrors BlogArticleMeta pattern ── */}
        <ContactPrenatalMeta />

        <BgBlob className="b1" aria-hidden="true" />
        <BgBlob className="b2" aria-hidden="true" />
        <BgBlob className="b3" aria-hidden="true" />
        <DotGrid aria-hidden="true" />

        {/* ── Hero ── */}
        <HeroContent>
          <HeroEyebrow>✦ Maternal Wellness</HeroEyebrow>
          <HeroTitle>Yoga for Every Stage of Motherhood</HeroTitle>
          <HeroDivider aria-hidden="true" />
          <HeroDesc>
            Whether you're expecting or recovering, our certified instructors
            design personalised yoga sessions to support your body and mind
            through every beautiful stage of motherhood.
          </HeroDesc>
        </HeroContent>

        <Inner>
          <FormBlock $delay={0.1}>
            <FormLayout>

              {/* ── Left image panel — image swaps with toggle ── */}
              <SidePanel>
                <SidePanelImg
                  src={currentImage}
                  alt={imageAlt}
                  loading="lazy"
                  key={currentImage}
                />
                <SidePanelOverlay aria-hidden="true" />
                <SidePanelMeta>
                  <PanelEyebrow>
                    ✦ {isPrenatal ? "Prenatal" : "Postnatal"} Yoga
                  </PanelEyebrow>
                  <PanelTitle>
                    {isPrenatal
                      ? "Nurture You & Your Baby"
                      : "Restore & Rebuild After Birth"}
                  </PanelTitle>
                  <PanelAccent aria-hidden="true" />
                  <PanelContact
                    href="tel:+917838866331"
                    aria-label="Call us"
                    onClick={() => analytics.trackPhoneClick("prenatal", "panel")}
                  >
                    <PhoneIcon />
                    +91-78388 66331
                  </PanelContact>
                </SidePanelMeta>
              </SidePanel>

              {/* ── Right form ── */}
              <FormContent>
                <FormTitle>Contact Us for Yoga Classes</FormTitle>
                <FormSubtitle>
                  Fields marked * are required. Switch between Prenatal and Postnatal below.
                </FormSubtitle>

                {/* ── Prenatal / Postnatal toggle ── */}
                <ToggleRow role="group" aria-label="Select yoga type">
                  <ToggleBtn
                    type="button"
                    $active={isPrenatal}
                    onClick={() => handleToggle(true)}
                    aria-pressed={isPrenatal}
                  >
                    🤰 Prenatal
                  </ToggleBtn>
                  <ToggleBtn
                    type="button"
                    $active={!isPrenatal}
                    onClick={() => handleToggle(false)}
                    aria-pressed={!isPrenatal}
                  >
                    🍼 Postnatal
                  </ToggleBtn>
                </ToggleRow>

                <form onSubmit={handleSubmit} noValidate>
                  {/* Row 1: Name */}
                  <FieldGrid $cols={1}>
                    <FieldWrap>
                      <FieldLabel htmlFor="pname">Full Name *</FieldLabel>
                      <InputField
                        id="pname" type="text" name="name"
                        placeholder="e.g. Priya Sharma"
                        value={formData.name}
                        onChange={handleChange}
                        onFocus={() => analytics.trackFieldFocus("name", "prenatal")}
                        $error={!!errors.name}
                        aria-required="true"
                      />
                      {errors.name && <FieldError role="alert">{errors.name}</FieldError>}
                    </FieldWrap>
                  </FieldGrid>

                  {/* Row 2: Email */}
                  <FieldGrid $cols={1}>
                    <FieldWrap>
                      <FieldLabel htmlFor="pemail">Email Address *</FieldLabel>
                      <InputField
                        id="pemail" type="email" name="email"
                        placeholder="you@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        onFocus={() => analytics.trackFieldFocus("email", "prenatal")}
                        $error={!!errors.email}
                        aria-required="true"
                      />
                      {errors.email && <FieldError role="alert">{errors.email}</FieldError>}
                    </FieldWrap>
                  </FieldGrid>

                  {/* Row 3: Country + Mobile */}
                  <FieldGrid $cols={2}>
                    <FieldWrap>
                      <FieldLabel htmlFor="pcountry">Country Code *</FieldLabel>
                      <InputField
                        id="pcountry" type="text" name="country"
                        placeholder="+91"
                        value={formData.country}
                        onChange={handleChange}
                        onFocus={() => analytics.trackFieldFocus("country", "prenatal")}
                        $error={!!errors.country}
                        aria-required="true"
                      />
                      {errors.country && <FieldError role="alert">{errors.country}</FieldError>}
                    </FieldWrap>

                    <FieldWrap>
                      <FieldLabel htmlFor="pmobile">Mobile Number *</FieldLabel>
                      <InputField
                        id="pmobile" type="tel" name="mobile"
                        placeholder="+91 88888 00000"
                        value={formData.mobile}
                        onChange={handleChange}
                        onFocus={() => analytics.trackFieldFocus("mobile", "prenatal")}
                        $error={!!errors.mobile}
                        aria-required="true"
                      />
                      {errors.mobile && <FieldError role="alert">{errors.mobile}</FieldError>}
                    </FieldWrap>
                  </FieldGrid>

                  {/* ── Dynamic fields based on Prenatal / Postnatal ── */}
                  <DynamicFields key={isPrenatal ? "prenatal" : "postnatal"}>
                    {isPrenatal ? (
                      <FieldGrid $cols={1}>
                        <FieldWrap>
                          <FieldLabel htmlFor="pregnancyDetails">
                            Pregnancy Stage
                          </FieldLabel>
                          <TextAreaField
                            id="pregnancyDetails"
                            name="pregnancyDetails"
                            placeholder="How far along are you? (e.g. 20 weeks, second trimester)"
                            value={formData.pregnancyDetails}
                            onChange={handleChange}
                            onFocus={() => analytics.trackFieldFocus("pregnancyDetails", "prenatal")}
                          />
                          <FieldHint>Optional — helps us personalise your session</FieldHint>
                        </FieldWrap>

                        <FieldWrap>
                          <FieldLabel htmlFor="complications">
                            Symptoms or Complications
                          </FieldLabel>
                          <TextAreaField
                            id="complications"
                            name="complications"
                            placeholder="Any symptoms or concerns you'd like to address?"
                            value={formData.complications}
                            onChange={handleChange}
                            onFocus={() => analytics.trackFieldFocus("complications", "prenatal")}
                          />
                          <FieldHint>Optional — confidential and used only for session planning</FieldHint>
                        </FieldWrap>
                      </FieldGrid>
                    ) : (
                      <FieldGrid $cols={1}>
                        <FieldWrap>
                          <FieldLabel htmlFor="babyAge">Baby's Age</FieldLabel>
                          <InputField
                            id="babyAge" type="text" name="babyAge"
                            placeholder="e.g. 6 weeks, 3 months"
                            value={formData.babyAge}
                            onChange={handleChange}
                            onFocus={() => analytics.trackFieldFocus("babyAge", "prenatal")}
                          />
                          <FieldHint>Optional — helps us select safe postures</FieldHint>
                        </FieldWrap>

                        <FieldWrap>
                          <FieldLabel htmlFor="postpartumConcerns">
                            Postpartum Concerns
                          </FieldLabel>
                          <TextAreaField
                            id="postpartumConcerns"
                            name="postpartumConcerns"
                            placeholder="Any postpartum symptoms or concerns? (back pain, pelvic floor, fatigue…)"
                            value={formData.postpartumConcerns}
                            onChange={handleChange}
                            onFocus={() => analytics.trackFieldFocus("postpartumConcerns", "prenatal")}
                          />
                        </FieldWrap>
                      </FieldGrid>
                    )}
                  </DynamicFields>

                  <SubmitBtn
                    type="submit"
                    disabled={loading}
                    aria-busy={loading}
                  >
                    {loading
                      ? <><SpinnerRing aria-hidden="true" /> Submitting…</>
                      : <>Submit <ArrowIcon /></>
                    }
                  </SubmitBtn>
                </form>

                <ContactLine>
                  <PhoneIcon aria-hidden="true" />
                  Or call us at{" "}
                  <a
                    href="tel:+917838866331"
                    aria-label="Call +91 78388 66331"
                    onClick={() => analytics.trackPhoneClick("prenatal", "footer_line")}
                  >
                    +91-78388 66331
                  </a>
                </ContactLine>
              </FormContent>
            </FormLayout>
          </FormBlock>
        </Inner>
      </Section>

      {/* ── Success modal ── */}
      <Modal
        show={showModal}
        onHide={handleModalClose}
        centered
        backdrop="static"
        aria-labelledby="prenatal-success-title"
      >
        <Modal.Body style={{ padding: 0 }}>
          <SuccessWrap>
            <SuccessIcon>
              <CheckIcon />
            </SuccessIcon>
            <SuccessTitle id="prenatal-success-title">
              We've Got Your Message!
            </SuccessTitle>
            <SuccessDesc>
              <strong>Thank you for reaching out.</strong>
              <br />
              Our maternal yoga specialist will contact you shortly to discuss
              your personalised programme.
            </SuccessDesc>
          </SuccessWrap>
        </Modal.Body>
      </Modal>
    </>
  );
};

ContactUSPrenatal.displayName = "ContactUSPrenatal";
export default React.memo(ContactUSPrenatal);